// client/src/components/campaign/AttributionPanel.jsx
//
// Multi-touch attribution for a campaign (On-Screen 2.0). Shows how many
// touches led to how many conversions, and credits conversion value under
// first / last / linear models + a per-channel split.

import React, { useCallback, useEffect, useState } from 'react';
import { getJson } from '../../services/http';

const MODEL_LABEL = { first: 'First touch', last: 'Last touch', linear: 'Linear' };
const CHANNEL_LABEL = { social: 'Social', email: 'Email', direct: 'Direct', referral: 'Referral' };

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AttributionPanel({ tenantId, campaignId }) {
  const [data, setData] = useState(null);
  const [model, setModel] = useState('linear');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJson(`/api/tenants/${Number(tenantId)}/campaigns/${Number(campaignId)}/attribution`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, campaignId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data || !data.available) {
    return (
      <section className="rounded-lg border border-black/10 p-4 text-sm opacity-60 dark:border-white/10">
        <h3 className="mb-1 text-sm font-semibold opacity-100">Attribution</h3>
        Multi-touch attribution needs a database migration before it can show data.
      </section>
    );
  }

  const total = Math.max(1, ...Object.values(data.valueByChannel || { x: 1 }));

  return (
    <section className="rounded-lg border border-black/10 p-4 dark:border-white/10">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Attribution</h3>
        <div className="flex gap-1 text-xs">
          {Object.keys(MODEL_LABEL).map((m) => (
            <button key={m} onClick={() => setModel(m)}
              className={`rounded-full px-2 py-0.5 ${model === m ? 'bg-cobalt text-white' : 'bg-black/5 dark:bg-white/10'}`}>
              {MODEL_LABEL[m]}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div><div className="text-lg font-semibold">{data.touches}</div><div className="opacity-60">touchpoints</div></div>
        <div><div className="text-lg font-semibold">{data.conversions}</div><div className="opacity-60">conversions</div></div>
        <div><div className="text-lg font-semibold">{money(data.models?.[model])}</div><div className="opacity-60">credited ({MODEL_LABEL[model].toLowerCase()})</div></div>
      </div>

      <div className="space-y-1">
        {['social', 'email', 'direct', 'referral'].filter((c) => (data.valueByChannel?.[c] || data.touchesByChannel?.[c])).map((c) => {
          const v = data.valueByChannel?.[c] || 0;
          return (
            <div key={c}>
              <div className="flex justify-between text-xs">
                <span>{CHANNEL_LABEL[c]}</span>
                <span className="opacity-70">{money(v)}{data.touchesByChannel?.[c] != null ? ` · ${data.touchesByChannel[c]} touches` : ''}</span>
              </div>
              <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                <div className="h-full rounded-full bg-cobalt" style={{ width: `${Math.round((v / total) * 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-xs opacity-50">
        Linear splits value across every campaign a converting visitor touched in the {data.lookbackDays}-day window before buying.
      </p>
    </section>
  );
}
